<script setup lang="ts">
import type { EntryCollection, EntrySkeletonType } from 'contentful'
import { getSeoPanelData } from '~/helpers/seo'
const path = useRoute().path
const slug = path.split('/').pop() || ''
const locale = useI18n().locale.value
const { getEntries } = useContentful()

const { isPreviewEnabled } = useContentfulPreview()
const previewStore = useContentfulPreviewStore()

const { data: articleData, execute: executeArticle } = await useAsyncData(`blogArticle:${slug}`, handler, {
  transform,
  deep: false,
  immediate: false,
})

async function handler() {
  const entries = await getEntries({
    content_type: 'blogArticle',
    'fields.slug': slug,
    limit: 1,
    locale: localeConfig[locale].code,
  })
  return entries
}

function transform(entries: EntryCollection<EntrySkeletonType, undefined, string> | undefined) {
  if (!entries || !entries.items) {
    return {}
  }
  const { items, includes } = entries
  const item = items?.[0]
  if (!item) return {}

  let fields = item.fields

  const seo = getSeoPanelData(fields.seoPanel, includes)
  const { articlePanel, author, belowContentPanels, ...restOfFields } = fields as any
  const panels = belowContentPanels
    ?.filter((panel: any) => panel.sys.contentType) // remove invalid items
    ?.map((panel: any) => ({
      ...panel.fields,
      id: panel.sys.id,
      componentName: panel.sys.contentType?.sys.id,
    }))

  fields = { author: author?.fields, articlePanel: articlePanel?.fields, belowContentPanels: panels, ...restOfFields }
  const contentType = item.sys.contentType?.sys.id

  if (isPreviewEnabled) previewStore.entries = entries

  return {
    fields: fields as any,
    seo,
    contentType,
  }
}

async function pillarHandler() {
  const entries = await getEntries({
    content_type: 'connectedContentPillar',
    'fields.slug': slug,
    limit: 1,
    locale: localeConfig[locale].code,
  })
  return entries
}

function pillarTransform(entries: EntryCollection<EntrySkeletonType, undefined, string> | undefined) {
  if (!entries || !entries.items) {
    return { fields: {}, seo: {}, contentType: '' }
  }
  const { items, includes } = entries
  const item = items?.[0]
  if (!item) return { fields: {}, seo: {}, contentType: '' }

  let fields = item.fields
  const seo = getSeoPanelData(fields.seoPanel, includes)
  const contentType = item.sys.contentType?.sys.id

  if (isPreviewEnabled) previewStore.entries = entries

  return {
    fields: fields as any,
    seo,
    contentType,
  }
}

const { data: pillarData, execute: executePillar } = await useAsyncData(
  `connectedContentPillar:${slug}`,
  pillarHandler,
  {
    transform: pillarTransform,
    deep: false,
    immediate: false,
  },
)

await executePillar()
if (!pillarData.value?.contentType) {
  await executeArticle()
}

const mappedContentType = mapContentfulContentTypeToPageContentType(
  pillarData.value?.contentType ?? articleData.value?.contentType ?? 'unknown',
  slug,
)
usePageContentType().setContentType(mappedContentType)

onMounted(() => {
  onMountedFirstLoadHandler()
})
</script>

<template>
  <BreadCrumbs :path="path" class="container" />
  <ArticlePage v-if="articleData?.contentType === 'blogArticle'" v-bind="articleData" />
  <ContentPillarPage v-if="pillarData?.contentType === 'connectedContentPillar'" v-bind="pillarData" />
</template>

<style lang="postcss"></style>
